import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { useAuth, homePathFor } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PrototypeBanner } from "@/components/PrototypeBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, User, Stethoscope, AlertCircle, ArrowLeft, Info } from "lucide-react";

const DEMO = [
  { label: "Paziente", email: "anna.verdi@demo.etruriacare.it", pw: "Demo1234!" },
  { label: "Professionista", email: "alessandro.conti@demo.etruriacare.it", pw: "Demo1234!" },
  { label: "Admin", email: "lucio2001merelli@gmail.com", pw: "EtruriaAdmin2026!" },
];

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [role, setRole] = useState(params.get("role") === "professional" ? "professional" : "patient");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [consents, setConsents] = useState({ privacy: false, terms: false, communications: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : (location.state?.from || homePathFor(user.role)), { replace: true });
  }, [user]); // eslint-disable-line

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success("Accesso effettuato!");
        navigate(u.role === "admin" ? "/admin" : (location.state?.from || homePathFor(u.role)), { replace: true });
      } else if (mode === "register") {
        if (!consents.privacy || !consents.terms) { setError("Devi accettare privacy e termini del servizio."); setLoading(false); return; }
        const u = await register({
          role, first_name: form.first_name, last_name: form.last_name, email: form.email,
          phone: form.phone, password: form.password,
          accept_privacy: consents.privacy, accept_terms: consents.terms, accept_communications: consents.communications,
        });
        toast.success("Registrazione completata!");
        navigate(homePathFor(u.role), { replace: true });
      } else {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Se l'email esiste, riceverai le istruzioni di recupero.");
        setMode("login");
      }
    } catch (err) {
      setError(apiError(err));
    } finally { setLoading(false); }
  };

  const fillDemo = (d) => { setMode("login"); setForm({ ...form, email: d.email, password: d.pw }); };

  return (
    <div className="min-h-screen bg-[#EAF3EF] flex flex-col">
      <PrototypeBanner />
      <div className="flex-1 grid lg:grid-cols-2">
        {/* left panel */}
        <div className="hidden lg:flex flex-col justify-between bg-[#102A2A] text-white p-12">
          <Logo to="/" light />
          <div>
            <h2 className="font-serif-accent text-3xl font-bold text-white leading-tight">La cura sanitaria torna a casa tua.</h2>
            <p className="mt-4 text-[#EAF3EF]/75 leading-relaxed max-w-md">
              Accedi alla tua area personale per richiedere prestazioni a domicilio o per gestire i tuoi incarichi come professionista.
            </p>
            <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Info className="h-4 w-4 text-[#087A73]" /> Credenziali demo</p>
              <div className="space-y-2">
                {DEMO.map((d) => (
                  <button key={d.email} onClick={() => fillDemo(d)} data-testid={`demo-fill-${d.label.toLowerCase()}`}
                    className="w-full text-left text-xs bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 transition-colors">
                    <span className="font-bold text-[#087A73]">{d.label}:</span> <span className="text-[#EAF3EF]/80">{d.email}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Link to="/" className="text-sm text-[#EAF3EF]/60 hover:text-white flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Torna al sito</Link>
        </div>

        {/* form */}
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-6 flex justify-center"><Logo to="/" /></div>
            <div className="bg-white rounded-2xl border border-[#D2E3DB] shadow-sm p-6 sm:p-8">
              <h1 className="text-2xl font-bold text-[#102A2A] mb-1">
                {mode === "login" ? "Accedi" : mode === "register" ? "Crea un account" : "Recupera password"}
              </h1>
              <p className="text-sm text-[#5C6B68] mb-6">
                {mode === "login" ? "Bentornato su EtruriaCare." : mode === "register" ? "Registrati per iniziare." : "Inserisci la tua email per il recupero."}
              </p>

              {mode === "register" && (
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[{ v: "patient", l: "Paziente / Familiare", i: User }, { v: "professional", l: "Professionista", i: Stethoscope }].map((r) => {
                    const Icon = r.i;
                    return (
                      <button key={r.v} type="button" onClick={() => setRole(r.v)} data-testid={`role-${r.v}`}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          role === r.v ? "border-[#087A73] bg-[#E0EFEA]" : "border-[#D2E3DB] hover:border-[#087A73]/40"}`}>
                        <Icon className={`h-6 w-6 ${role === r.v ? "text-[#087A73]" : "text-[#5C6B68]"}`} />
                        <span className={`text-xs font-semibold text-center ${role === r.v ? "text-[#087A73]" : "text-[#5C6B68]"}`}>{r.l}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {error && (
                <div data-testid="auth-error" className="mb-4 flex items-start gap-2 bg-[#F9E3E0] border border-[#C0392B]/30 text-[#C0392B] rounded-xl px-4 py-3 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="fn">Nome</Label><Input id="fn" data-testid="register-firstname" required value={form.first_name} onChange={upd("first_name")} className="h-12 rounded-xl mt-1" /></div>
                    <div><Label htmlFor="ln">Cognome</Label><Input id="ln" data-testid="register-lastname" required value={form.last_name} onChange={upd("last_name")} className="h-12 rounded-xl mt-1" /></div>
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" required value={form.email} onChange={upd("email")} className="h-12 rounded-xl mt-1" placeholder="nome@esempio.it" />
                </div>
                {mode === "register" && (
                  <div><Label htmlFor="phone">Telefono</Label><Input id="phone" data-testid="register-phone" required value={form.phone} onChange={upd("phone")} className="h-12 rounded-xl mt-1" placeholder="333 1234567" /></div>
                )}
                {mode !== "forgot" && (
                  <div>
                    <Label htmlFor="pw">Password</Label>
                    <Input id="pw" data-testid="login-password" type="password" required value={form.password} onChange={upd("password")} className="h-12 rounded-xl mt-1" placeholder="Almeno 8 caratteri" />
                  </div>
                )}

                {mode === "register" && (
                  <div className="space-y-3 pt-1">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox data-testid="consent-privacy" checked={consents.privacy} onCheckedChange={(v) => setConsents({ ...consents, privacy: !!v })} className="mt-0.5" />
                      <span className="text-xs text-[#5C6B68]">Accetto la <Link to="/legal/privacy" target="_blank" className="text-[#087A73] font-semibold underline">Privacy Policy</Link> *</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox data-testid="consent-terms" checked={consents.terms} onCheckedChange={(v) => setConsents({ ...consents, terms: !!v })} className="mt-0.5" />
                      <span className="text-xs text-[#5C6B68]">Accetto i <Link to="/legal/termini" target="_blank" className="text-[#087A73] font-semibold underline">Termini del servizio</Link> *</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox data-testid="consent-comms" checked={consents.communications} onCheckedChange={(v) => setConsents({ ...consents, communications: !!v })} className="mt-0.5" />
                      <span className="text-xs text-[#5C6B68]">Acconsento a ricevere comunicazioni operative (facoltativo)</span>
                    </label>
                  </div>
                )}

                <Button type="submit" data-testid="auth-submit" disabled={loading}
                  className="w-full bg-[#087A73] hover:bg-[#06615B] text-white rounded-xl h-13 min-h-[52px] font-semibold text-base">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Accedi" : mode === "register" ? "Registrati" : "Invia richiesta"}
                </Button>
              </form>

              <div className="mt-5 text-center text-sm text-[#5C6B68] space-y-2">
                {mode === "login" && (
                  <>
                    <button onClick={() => { setMode("forgot"); setError(""); }} data-testid="link-forgot" className="text-[#087A73] font-semibold hover:underline">Password dimenticata?</button>
                    <p>Non hai un account? <button onClick={() => { setMode("register"); setError(""); }} data-testid="link-register" className="text-[#087A73] font-semibold hover:underline">Registrati</button></p>
                  </>
                )}
                {mode === "register" && (
                  <p>Hai già un account? <button onClick={() => { setMode("login"); setError(""); }} data-testid="link-login" className="text-[#087A73] font-semibold hover:underline">Accedi</button></p>
                )}
                {mode === "forgot" && (
                  <button onClick={() => { setMode("login"); setError(""); }} className="text-[#087A73] font-semibold hover:underline">Torna all'accesso</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
